// Match backend NotificationType enum exactly
export enum NotificationType {
    // ============================================
    // SYSTEM SECURITY
    // ============================================
    DEVICE_AUTHENTICATION_ALERT = 'DEVICE_AUTHENTICATION_ALERT',
    LOGIN_ACTIVITY = 'LOGIN_ACTIVITY',
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',
    FAILED_LOGIN_ATTEMPT = 'FAILED_LOGIN_ATTEMPT',
    MULTIPLE_FAILED_LOGINS = 'MULTIPLE_FAILED_LOGINS',
    PASSWORD_CHANGED = 'PASSWORD_CHANGED',
    PASSWORD_RESET_REQUESTED = 'PASSWORD_RESET_REQUESTED',
    PERMISSION_CHANGE = 'PERMISSION_CHANGE',
    ACCOUNT_LOCKOUT = 'ACCOUNT_LOCKOUT',
    USER_ROLE_MODIFIED = 'USER_ROLE_MODIFIED',
    SYSTEM_CONFIG_CHANGE = 'SYSTEM_CONFIG_CHANGE',
    SUSPICIOUS_ACTIVITY_DETECTED = 'SUSPICIOUS_ACTIVITY_DETECTED',
    NEW_DEVICE_LOGIN = 'NEW_DEVICE_LOGIN',

    // ============================================
    // PROJECT LIFECYCLE
    // ============================================
    PROJECT_CREATED = 'PROJECT_CREATED',
    DRAFT_PROJECT_CREATED = 'DRAFT_PROJECT_CREATED',
    DRAFT_PROJECT_APPROVAL_REQUEST = 'DRAFT_PROJECT_APPROVAL_REQUEST',
    PROJECT_APPROVED = 'PROJECT_APPROVED',
    PROJECT_REJECTED = 'PROJECT_REJECTED',
    PROJECT_STATUS_CHANGED = 'PROJECT_STATUS_CHANGED',
    PROJECT_TIMELINE_UPDATED = 'PROJECT_TIMELINE_UPDATED',
    PROJECT_DEADLINE_APPROACHING_48H = 'PROJECT_DEADLINE_APPROACHING_48H',
    PROJECT_DEADLINE_APPROACHING_24H = 'PROJECT_DEADLINE_APPROACHING_24H',
    PROJECT_DEADLINE_APPROACHING_8H = 'PROJECT_DEADLINE_APPROACHING_8H',
    PROJECT_OVERDUE = 'PROJECT_OVERDUE',
    PROJECT_COMPLETED = 'PROJECT_COMPLETED',
    PROJECT_ASSIGNED = 'PROJECT_ASSIGNED',

    // ============================================
    // TASK MANAGEMENT
    // ============================================
    TASK_ASSIGNED = 'TASK_ASSIGNED',
    TASK_REASSIGNED = 'TASK_REASSIGNED',
    TASK_PRIORITY_CHANGED = 'TASK_PRIORITY_CHANGED',
    TASK_DUE_DATE_MODIFIED = 'TASK_DUE_DATE_MODIFIED',
    TASK_STATUS_UPDATED = 'TASK_STATUS_UPDATED',
    TASK_COMPLETED = 'TASK_COMPLETED',
    TASK_COMMENT_ADDED = 'TASK_COMMENT_ADDED',
    TASK_OVERDUE = 'TASK_OVERDUE',

    // ============================================
    // DOCUMENT WORKFLOW
    // ============================================
    DOCUMENT_UPLOADED = 'DOCUMENT_UPLOADED',
    DOCUMENT_PROCESSING_COMPLETED = 'DOCUMENT_PROCESSING_COMPLETED',
    DOCUMENT_PROCESSING_FAILED = 'DOCUMENT_PROCESSING_FAILED',
    DOCUMENT_PROCESSING_REJECTED = 'DOCUMENT_PROCESSING_REJECTED',
    DOCUMENT_PROCESSING_STALLED = 'DOCUMENT_PROCESSING_STALLED',
    DOCUMENT_REVIEW_REQUEST = 'DOCUMENT_REVIEW_REQUEST',
    DOCUMENT_APPROVAL_REQUEST = 'DOCUMENT_APPROVAL_REQUEST',
    DOCUMENT_APPROVED = 'DOCUMENT_APPROVED',
    DOCUMENT_REJECTED = 'DOCUMENT_REJECTED',
    DOCUMENT_VERSION_UPDATED = 'DOCUMENT_VERSION_UPDATED',
    DOCUMENT_COMMENT_ADDED = 'DOCUMENT_COMMENT_ADDED',
    DOCUMENT_SHARED = 'DOCUMENT_SHARED',

    // ============================================
    // SAMPLE MANAGEMENT
    // ============================================
    SAMPLE_REQUEST_CREATED = 'SAMPLE_REQUEST_CREATED',
    SAMPLE_COLLECTION_CONFIRMED = 'SAMPLE_COLLECTION_CONFIRMED',
    SAMPLE_READY_FOR_INSPECTION = 'SAMPLE_READY_FOR_INSPECTION',
    LAB_TEST_STATUS_UPDATED = 'LAB_TEST_STATUS_UPDATED',
    TEST_RESULT_AVAILABLE = 'TEST_RESULT_AVAILABLE',
    OUT_OF_SPEC_ALERT = 'OUT_OF_SPEC_ALERT',
    SAMPLE_INSPECTION_COMPLETED = 'SAMPLE_INSPECTION_COMPLETED',

    // ============================================
    // DEVIATION & COMPLIANCE
    // ============================================
    DEVIATION_DETECTED = 'DEVIATION_DETECTED',
    QUALITY_EXCEPTION_ESCALATED = 'QUALITY_EXCEPTION_ESCALATED',
    COMPLIANCE_VIOLATION_DETECTED = 'COMPLIANCE_VIOLATION_DETECTED',
    DEVIATION_INVESTIGATION_STARTED = 'DEVIATION_INVESTIGATION_STARTED',
    DEVIATION_RESOLVED = 'DEVIATION_RESOLVED',
    REGULATORY_REPORTING_REQUIRED = 'REGULATORY_REPORTING_REQUIRED',

    // ============================================
    // COLLABORATION
    // ============================================
    MENTION = 'MENTION',
    DISCUSSION_THREAD_UPDATED = 'DISCUSSION_THREAD_UPDATED',
    COMMENT_REPLY = 'COMMENT_REPLY',
    COMMENT_REACTION = 'COMMENT_REACTION',
    TEAM_ASSIGNMENT_CHANGED = 'TEAM_ASSIGNMENT_CHANGED',
    COLLABORATION_INVITE = 'COLLABORATION_INVITE',

    // ============================================
    // SYSTEM
    // ============================================
    SYSTEM_ANNOUNCEMENT = 'SYSTEM_ANNOUNCEMENT',
    MAINTENANCE_SCHEDULED = 'MAINTENANCE_SCHEDULED',
    SYSTEM_UPDATE_AVAILABLE = 'SYSTEM_UPDATE_AVAILABLE',
}

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export enum NotificationCategory {
    SYSTEM_SECURITY = 'SYSTEM_SECURITY',
    PROJECT_LIFECYCLE = 'PROJECT_LIFECYCLE',
    TASK_MANAGEMENT = 'TASK_MANAGEMENT',
    DOCUMENT_WORKFLOW = 'DOCUMENT_WORKFLOW',
    SAMPLE_MANAGEMENT = 'SAMPLE_MANAGEMENT',
    DEVIATION_COMPLIANCE = 'DEVIATION_COMPLIANCE',
    COLLABORATION = 'COLLABORATION',
    SYSTEM = 'SYSTEM',
}

export interface Notification {
    id: string;
    userId: string;
    type: NotificationType;
    category: NotificationCategory;
    priority: NotificationPriority;
    title: string;
    message: string;
    metadata: Record<string, any>;
    actionUrl: string | null;
    relatedEntityId: string | null;
    relatedEntityType: string | null;
    isRead: boolean;
    isArchived?: boolean;
    readAt: Date | null;
    createdAt: Date;
    updatedAt?: Date;
}

export interface NotificationStats {
    total: number;
    unread: number;
    read: number;
    byCategory: Record<NotificationCategory, number>;
    byPriority: Record<string, number>;
    recentActivity: {
        last24Hours: number;
        last7Days: number;
        last30Days: number;
    };
}

export interface EmailPreferences {
    enabled: boolean;
    priorities: NotificationPriority[];
}

export interface SmsPreferences {
    enabled: boolean;
    priorities: NotificationPriority[];
}

export interface NotificationPreferences {
    id: string;
    userId: string;
    channels: {
        inApp: boolean;
        email: EmailPreferences;
        sms: SmsPreferences;
    };
    enabledCategories: NotificationCategory[];
    quietHoursEnabled: boolean;
    quietHoursStart: string | null;
    quietHoursEnd: string | null;
    groupByContext: boolean;
    desktopNotificationsEnabled: boolean;
    soundEnabled: boolean;
    createdAt: Date;
    updatedAt: Date;
}

export interface SecurityEvent {
    id: string;
    notificationId: string;
    eventType: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    userId: string | null;
    userEmail: string | null;
    ipAddress: string | null;
    userAgent: string | null;
    location: string | null;
    description: string;
    additionalData: Record<string, any>;
    timestamp: Date;
    createdAt: Date;
    notification?: {
        title: string;
        message: string;
        priority: string;
        [key: string]: any;
    };
    user?: {
        email: string;
        [key: string]: any;
    };
}

export interface NotificationQueryParams {
    page?: number;
    limit?: number;
    isRead?: boolean;
    isArchived?: string;
    category?: NotificationCategory;
    priority?: NotificationPriority;
    startDate?: string;
    endDate?: string;
}

export interface UpdatePreferencesDto {
    channels?: {
        inApp?: boolean;
        email?: EmailPreferences;
        sms?: SmsPreferences;
    };
    enabledCategories?: NotificationCategory[];
    quietHoursEnabled?: boolean;
    quietHoursStart?: string;
    quietHoursEnd?: string;
    groupByContext?: boolean;
    desktopNotifications?: boolean;
    soundEnabled?: boolean;
    securityDigestEnabled?: boolean;
}





